/**
 * Fleet Health Card — compact horizontal bar chart showing system status distribution.
 * Online / Warning / Offline / Stale counts with proportional bars.
 * Tier 1 card (always visible).
 */

import type { FleetSummaryCounts } from '@/lib/fleet-severity';
import { t } from '@/lib/vocabulary';
import { useApp } from '@/contexts/AppContext';

interface FleetHealthCardProps {
  counts: FleetSummaryCounts;
  onFilterClick?: (status: string) => void;
}

const STATUS_ROWS = [
  { key: 'online' as const, vocabKey: 'live', color: 'bg-status-online', textColor: 'text-status-online' },
  { key: 'warning' as const, vocabKey: 'watch', color: 'bg-wt-amber-primary', textColor: 'text-wt-amber-primary' },
  { key: 'offline' as const, vocabKey: 'offline', color: 'bg-status-offline', textColor: 'text-status-offline' },
  { key: 'stale' as const, vocabKey: 'stale', color: 'bg-wt-text-muted', textColor: 'text-wt-text-muted' },
] as const;

function getCount(counts: FleetSummaryCounts, key: string): number {
  if (key === 'online') return counts.stable;
  if (key === 'warning') return counts.critical + counts.watch;
  if (key === 'offline') return counts.offline;
  // stale — not tracked separately in FleetSummaryCounts, show 0
  return 0;
}

export function FleetHealthCard({ counts, onFilterClick }: FleetHealthCardProps) {
  const { isHomeowner } = useApp();
  const maxCount = Math.max(...STATUS_ROWS.map((r) => getCount(counts, r.key)), 1);

  return (
    <div className="flex-1 min-w-[240px] rounded-lg border border-border bg-card p-6 space-y-4">
      <span className="text-sm text-muted-foreground">System Health</span>
      <div className="space-y-2.5">
        {STATUS_ROWS.map((row) => {
          const count = getCount(counts, row.key);
          const pct = (count / maxCount) * 100;
          return (
            <button
              key={row.key}
              type="button"
              onClick={() => onFilterClick?.(row.key)}
              className="flex w-full items-center gap-3 group text-left hover:bg-wt-bg-tertiary/30 rounded px-1 py-0.5 -mx-1 transition-colors"
            >
              <span className="w-[60px] shrink-0 text-xs text-muted-foreground">{t(row.vocabKey, isHomeowner)}</span>
              <span className={`w-8 shrink-0 text-right font-mono text-sm font-semibold ${row.textColor}`}>
                {count}
              </span>
              <div className="flex-1 h-2 rounded bg-muted overflow-hidden">
                <div
                  className={`h-full rounded ${row.color} transition-all`}
                  style={{ width: `${pct}%` }}
                />
              </div>
            </button>
          );
        })}
      </div>
      <p className="text-xs text-muted-foreground font-mono">{counts.total} systems</p>
    </div>
  );
}
